import { ensureDefined } from '@avantia/client-and-server-utilities';
import { HpError } from '@avantia/logging-and-errors';
import { createControlId } from '@avantia/questionset-model';
import dayjs from 'dayjs';
import { getQuestionStandardTextAndStyle, getTextFromLookupMap } from '../../../engineClient/tools/utilities';
import {
  HpClientQuestionData,
  HpClientStandardInputTextAndStyle,
  HpInputControlProps
} from '../../../engineClient/types';
import { questionIds } from '../../../types/questionConstants';
import { HpClaimsQuestionSetPageProps } from '../../questionSetForm/questionSetPageTypes';

export interface ReadOnlySummaryCustomControlProps extends HpInputControlProps {
  pageProps: HpClaimsQuestionSetPageProps;
}

export interface ReadOnlySummary {
  questionsToDisplay: string[];
}

export function ReadOnlySummaryCustomControl(props: ReadOnlySummaryCustomControlProps): JSX.Element | null {
  const { question, answers, pageProps } = props;
  const { displayState, questions } = pageProps;
  const { questionsUI } = displayState;
  const { data } = question;
  const { customData } = data as HpClientQuestionData;

  const readOnlySummaryCustomData = ensureDefined(customData, 'customData') as unknown as ReadOnlySummary;
  const { questionsToDisplay } = readOnlySummaryCustomData;

  if (!questionsToDisplay || !questionsToDisplay.length) {
    throw new HpError('The question must contain list of question names to display in the summary.');
  }

  const summaryList: JSX.Element[] = [];

  questionsToDisplay.map((item, index) => {
    const questionId = questionIds[item];

    if (!questionId) {
      throw new HpError(`The question name ${item} is not valid.`);
    }

    const { isVisible } = questionsUI[questionId];

    if (isVisible) {
      let updatedAnswer: string | null = null;

      const { customer: answer } = answers[questionId];
      if (answer !== null) {
        const questionToDisplay = questions[questionId];
        const { questionText } = getQuestionStandardTextAndStyle(
          questionToDisplay
        ) as HpClientStandardInputTextAndStyle;
        const { data, dataType } = questionToDisplay;
        const { lookup } = data as HpClientQuestionData;
        const answerValue = answer as string;

        if (dataType === 'date') {
          updatedAnswer = dayjs(answerValue).format('DD MMM YYYY');
        } else if (lookup && lookup[answerValue]) {
          updatedAnswer = getTextFromLookupMap(answerValue, lookup);
        }

        summaryList.push(createSummary(index, questionId, questionText as string, updatedAnswer || answerValue));
      }
    }
  });

  return <div className="av-card read-only-summary">{summaryList}</div>;
}

function createSummary(key: number, questionId: string, questionText: string, answer: string): JSX.Element {
  const controllerKey = 'summary';
  return (
    <div key={key} className="av-card-child">
      <label id={createControlId({ questionId, key: controllerKey, options: { customPrefix: 'L' } })}>
        <strong>{questionText}</strong>
      </label>
      <div id={createControlId({ questionId, key: controllerKey })}>
        <span>{answer}</span>
      </div>
    </div>
  );
}
